import React, { createRef, Component } from 'react';
import L from 'leaflet';
import { MapContainer, ZoomControl, Marker, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';

import { setWorkerUrl } from 'maplibre-gl';
import { maplibreGL } from '@maplibre/maplibre-gl-leaflet';
import 'maplibre-gl/dist/maplibre-gl.css';

import { WidgetHelper } from './WidgetHelper';
import { IMapMarker } from '../Models';

setWorkerUrl('/maplibre/maplibre-gl-worker.mjs');

interface IMapViewerProps {
    className: string;
    markers: string;
}

interface IMapViewerState {
    height: string;
    isMobile: boolean;
    markers: IMapMarker[];
    openMap: boolean;
    zoom: number;
}

const OpenFreeMapLayer = () => {
    const map = useMap();

    React.useEffect(() => {
        const layer = maplibreGL({
            style: 'https://tiles.openfreemap.org/styles/positron',
        });

        layer.addTo(map);

        return () => {
            map.removeLayer(layer);
        };
    }, [map]);

    return null;
};

export class MapViewerWidget extends Component<
    IMapViewerProps,
    IMapViewerState
> {
    private mapRef = createRef<L.Map>();
    private markers: L.Marker[] = [];
    private iconDefault: L.Icon;

    constructor(props: IMapViewerProps) {
        super(props);

        this.state = {
            height: '100%',
            isMobile: window.innerWidth < 992,
            markers: JSON.parse(this.props.markers),
            openMap: false,
            zoom: 12,
        };

        this.iconDefault = new L.Icon({
            iconUrl: '/images/MapPin/pin_partner.svg',
            iconSize: new L.Point(22, 27),
        });
    }

    componentDidMount() {
        window.addEventListener('resize', this.onResize);
    }

    componentDidUpdate() {
        const map = this.mapRef.current;

        if (map) {
            map.invalidateSize();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.onResize);
    }

    bindMarker = (marker: L.Marker | null) => {
        if (marker) {
            this.markers.push(marker);
        }
    };

    closeMap = () => {
        this.setState({
            openMap: false,
            zoom: 12,
            height: '100%',
        });
    };

    handleMapClick = () => {
        if (this.state.isMobile && !this.state.openMap) {
            this.setState({
                openMap: true,
                zoom: 11,
                height: 'calc(100vh - 50px)',
            });
        }
    };

    onResize = () => {
        this.setState({
            isMobile: window.innerWidth < 992,
        });
    };

    render() {
        return (
            <div
                id="map"
                className={this.props.className}
                onClick={this.handleMapClick}
            >
                <MapContainer
                    ref={this.mapRef}
                    center={[50.84275, 4.35155]}
                    zoom={this.state.zoom}
                    scrollWheelZoom={false}
                    style={{ height: this.state.height }}
                    zoomControl={false}
                    maxZoom={19}
                >
                    <OpenFreeMapLayer />

                    <MarkerClusterGroup>
                        {this.state.markers.map((m, index) => {
                            if (!m.latitude || !m.longitude) {
                                return null;
                            }

                            return (
                                <Marker
                                    key={'officeMarker' + index}
                                    position={[m.latitude, m.longitude]}
                                    icon={this.iconDefault}
                                    ref={this.bindMarker}
                                />
                            );
                        })}
                    </MarkerClusterGroup>

                    {!this.state.isMobile && (
                        <ZoomControl position="bottomright" />
                    )}
                </MapContainer>
            </div>
        );
    }
}

WidgetHelper.register(MapViewerWidget, 'map-viewer-widget');
